import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [ "select", "label" ]
  static values = { channelsUrl: String, selectedId: String, selectedLabel: String }

  connect() {
    this.#loadChannels()
  }

  // Re-fetch, bypassing the server-side cache, to pick up channels created after
  // the list was last loaded. Toasts the result — without it, a refresh that
  // finds nothing new looks indistinguishable from a refresh that did nothing.
  async refresh(event) {
    const button = event.currentTarget
    button.disabled = true

    const select = this.#select
    Array.from(select.options).forEach((opt) => { if (opt.value) opt.remove() })

    try {
      const ok = await this.#loadChannels(true)
      window.toast(ok ? "Channel list refreshed" : "Couldn't refresh channel list", { kind: ok ? "success" : "error" })
    } finally {
      button.disabled = false
    }
  }

  syncLabel() {
    const selected = this.#select.selectedOptions[0]
    if (this.hasLabelTarget) {
      this.labelTarget.value = selected ? selected.text : ""
    }
  }

  // The controller mounts directly on the <select> in the routing matrix, or on
  // a wrapper (with a select target) where a refresh button sits beside it.
  get #select() {
    return this.hasSelectTarget ? this.selectTarget : this.element
  }

  async #loadChannels(force = false) {
    try {
      const url = force ? `${this.channelsUrlValue}?refresh=1` : this.channelsUrlValue
      const response = await fetch(url, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) {
        this.#showError()
        return false
      }
      const channels = await response.json()
      this.#populateSelect(channels)
      return true
    } catch {
      this.#showError()
      return false
    }
  }

  #showError() {
    const opt = document.createElement("option")
    opt.value = ""
    opt.textContent = "Failed to load channels — reconnect the integration"
    opt.disabled = true
    this.#select.appendChild(opt)
  }

  #populateSelect(channels) {
    const select = this.#select
    const current = select.value || this.selectedIdValue

    channels.forEach(({ id, name }) => {
      const opt = document.createElement("option")
      opt.value = id
      opt.textContent = `#${name}`
      if (id === current) opt.selected = true
      select.appendChild(opt)
    })

    this.syncLabel()
  }
}
